/**
 * DEV-1110 — this installation's stable id.
 *
 * Minted once, on first run, and kept for the life of the install. It is what
 * binds a device credential to this handset: re-enrolling with the same
 * installId rotates the existing credential rather than growing the grower's
 * device list a row per sign-in.
 *
 * Deliberately NOT a secret, and deliberately readable by the Boer module —
 * Settings → Devices marks which row in the fleet is the phone you are holding,
 * and it can only do that by comparing this value. The secret half of the
 * device identity lives in `deviceCredential.ts`, encrypted.
 *
 * Clearing site data mints a new one, which reads server-side as a new device;
 * the old row ages out at its `expiresAt` (30 days) or is revoked in Settings.
 * That is the correct trade: a device that cannot prove continuity is a new
 * device.
 */

const INSTALL_ID_KEY = "metaship:installId";

/**
 * The backend's own validation (`INSTALL_ID_RE`, `api-credentials-controller`):
 * 8-64 chars of `[A-Za-z0-9._-]`, leading alphanumeric. Checked here as well so
 * a value corrupted in localStorage is replaced locally instead of failing
 * every enrolment with a 400 the grower cannot act on.
 */
const INSTALL_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{7,63}$/;

function mint(): string {
  // `crypto.randomUUID` needs a secure context; a tenant app is always HTTPS,
  // but a dev host on plain http would otherwise throw here and take the whole
  // boot with it.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  // Last resort. Weak, and it only has to be UNIQUE, not unguessable — an
  // installId names a device, it never authorises one.
  return `i${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * This install's id, minting and persisting one on first call.
 *
 * Returns a fresh (unpersisted) id when storage is unavailable — a private
 * window still gets a working session, it just cannot stay enrolled.
 */
export function getInstallId(): string {
  try {
    const stored = localStorage.getItem(INSTALL_ID_KEY);
    if (stored && INSTALL_ID_RE.test(stored)) return stored;
    const fresh = mint();
    localStorage.setItem(INSTALL_ID_KEY, fresh);
    return fresh;
  } catch {
    return mint();
  }
}

/** Exported for the tests and for Settings → Devices ("this device"). */
export function isValidInstallId(value: string | null | undefined): boolean {
  return typeof value === "string" && INSTALL_ID_RE.test(value);
}
